import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ColorContext } from "../Context/ColorContext";
import { BackendContext } from "../Context/BackendContext";
import LoginRequiredModal from "./LogInRequiredModal";
import { Lock, RefreshCcw } from "lucide-react";
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@heroui/react";

const PREMIUM_COLOR_COSTS = {
  charcoal: 2000,
  silver: 2000,
  ivory: 2000,
  brick: 2000,
  amber: 2000,
  lemon: 2000,
  lime: 2000,
  royal: 2000,
  blush: 4000,
  amethyst: 4000,
  aqua: 4000,
  sage: 4000,
  hazel: 4000,
  apricot: 4000,
  denim: 4000,
  lavender: 4000,
  salmon: 7000,
  crimson: 7000,
  glacier: 7000,
  deepsea: 7000,
  iris: 7000,
  fuchsia: 7000,
  clay: 7000,
  moss: 7000,
  petal: 5000,
  powder: 5000,
  mist: 5000,
  butter: 5000,
  mintcream: 5000,
  lilac: 5000,
  pearl: 5000,
  softpeach: 5000,
  porcelain: 1000,
  fair: 1000,
  beige: 1000,
  warmbeige: 1000,
  honey: 1000,
  almond: 1000,
  umber: 1000,
  ebony: 1000,
};
const COLOR_PAGES = [[
  {
    key: 'guest',
    colors: [
      { id: 'ink', hex: '#111827', label: 'Ink', tier: 'guest' },
      { id: 'slate', hex: '#6B7280', label: 'Slate', tier: 'guest' },
      { id: 'cloud', hex: '#F3F4F6', label: 'Cloud', tier: 'guest' },
      { id: 'ember', hex: '#DC2626', label: 'Ember', tier: 'guest' },
      { id: 'gold', hex: '#F59E0B', label: 'Gold', tier: 'guest' },
      { id: 'sun', hex: '#FDE047', label: 'Sun', tier: 'guest' },
      { id: 'leaf', hex: '#22C55E', label: 'Leaf', tier: 'guest' },
      { id: 'sky', hex: '#2563EB', label: 'Sky', tier: 'guest' },
    ],
  },
  {
    key: 'member',
    colors: [
      { id: 'rose', hex: '#FB7185', label: 'Rose', tier: 'member' },
      { id: 'plum', hex: '#7C3AED', label: 'Plum', tier: 'member' },
      { id: 'teal', hex: '#14B8A6', label: 'Teal', tier: 'member' },
      { id: 'mint', hex: '#86EFAC', label: 'Mint', tier: 'member' },
      { id: 'cocoa', hex: '#92400E', label: 'Cocoa', tier: 'member' },
      { id: 'peach', hex: '#FB923C', label: 'Peach', tier: 'member' },
      { id: 'navy', hex: '#1D4ED8', label: 'Navy', tier: 'member' },
      { id: 'orchid', hex: '#C084FC', label: 'Orchid', tier: 'member' },
    ],
  },
  {
    key: 'premium',
    colors: [
      { id: 'coral', hex: '#F97360', label: 'Coral', tier: 'member' },
      { id: 'wine', hex: '#9F1239', label: 'Wine', tier: 'member' },
      { id: 'ice', hex: '#67E8F9', label: 'Ice', tier: 'member' },
      { id: 'lagoon', hex: '#0F766E', label: 'Lagoon', tier: 'member' },
      { id: 'violet', hex: '#8B5CF6', label: 'Violet', tier: 'member' },
      { id: 'magenta', hex: '#DB2777', label: 'Magenta', tier: 'member' },
      { id: 'sand', hex: '#D6D3D1', label: 'Sand', tier: 'member' },
      { id: 'forest', hex: '#14532D', label: 'Forest', tier: 'member' },
    ],
  },
], [
  {
    key: 'premium-a',
    colors: [
      { id: 'charcoal', hex: '#374151', label: 'Charcoal', tier: 'premium', paletteKey: 'mono' },
      { id: 'silver', hex: '#D1D5DB', label: 'Silver', tier: 'premium', paletteKey: 'mono' },
      { id: 'ivory', hex: '#FFF7D6', label: 'Ivory', tier: 'premium', paletteKey: 'mono' },
      { id: 'brick', hex: '#991B1B', label: 'Brick', tier: 'premium', paletteKey: 'ember' },
      { id: 'amber', hex: '#B45309', label: 'Amber', tier: 'premium', paletteKey: 'ember' },
      { id: 'lemon', hex: '#FFF176', label: 'Lemon', tier: 'premium', paletteKey: 'ember' },
      { id: 'lime', hex: '#65A30D', label: 'Lime', tier: 'premium', paletteKey: 'ember' },
      { id: 'royal', hex: '#1E40AF', label: 'Royal', tier: 'premium', paletteKey: 'ember' },
    ],
  },
  {
    key: 'premium-b',
    colors: [
      { id: 'blush', hex: '#FBCFE8', label: 'Blush', tier: 'premium', paletteKey: 'pastel' },
      { id: 'amethyst', hex: '#6D28D9', label: 'Amethyst', tier: 'premium', paletteKey: 'pastel' },
      { id: 'aqua', hex: '#0F766E', label: 'Aqua', tier: 'premium', paletteKey: 'pastel' },
      { id: 'sage', hex: '#4ADE80', label: 'Sage', tier: 'premium', paletteKey: 'pastel' },
      { id: 'hazel', hex: '#78350F', label: 'Hazel', tier: 'premium', paletteKey: 'earth' },
      { id: 'apricot', hex: '#FED7AA', label: 'Apricot', tier: 'premium', paletteKey: 'earth' },
      { id: 'denim', hex: '#60A5FA', label: 'Denim', tier: 'premium', paletteKey: 'earth' },
      { id: 'lavender', hex: '#E9D5FF', label: 'Lavender', tier: 'premium', paletteKey: 'earth' },
    ],
  },
  {
    key: 'premium-c',
    colors: [
      { id: 'salmon', hex: '#FDB4AF', label: 'Salmon', tier: 'premium', paletteKey: 'dawn' },
      { id: 'crimson', hex: '#7F1D1D', label: 'Crimson', tier: 'premium', paletteKey: 'dawn' },
      { id: 'glacier', hex: '#CFFAFE', label: 'Glacier', tier: 'premium', paletteKey: 'tide' },
      { id: 'deepsea', hex: '#164E63', label: 'Deep Sea', tier: 'premium', paletteKey: 'tide' },
      { id: 'iris', hex: '#A78BFA', label: 'Iris', tier: 'premium', paletteKey: 'arcade' },
      { id: 'fuchsia', hex: '#F0ABFC', label: 'Fuchsia', tier: 'premium', paletteKey: 'arcade' },
      { id: 'clay', hex: '#B45309', label: 'Clay', tier: 'premium', paletteKey: 'terra' },
      { id: 'moss', hex: '#4D7C0F', label: 'Moss', tier: 'premium', paletteKey: 'terra' },
    ],
  },
], [
  {
    key: 'skin-guest',
    colors: [
      { id: 'porcelain', hex: '#F7E3D4', label: 'Porcelain', tier: 'premium', paletteKey: 'skintones' },
      { id: 'fair', hex: '#EBCBB2', label: 'Fair', tier: 'premium', paletteKey: 'skintones' },
      { id: 'beige', hex: '#DDB193', label: 'Beige', tier: 'premium', paletteKey: 'skintones' },
      { id: 'warmbeige', hex: '#C8916B', label: 'Warm Beige', tier: 'premium', paletteKey: 'skintones' },
      { id: 'honey', hex: '#B97B52', label: 'Honey', tier: 'premium', paletteKey: 'skintones' },
      { id: 'almond', hex: '#9A6648', label: 'Almond', tier: 'premium', paletteKey: 'skintones' },
      { id: 'umber', hex: '#72462F', label: 'Umber', tier: 'premium', paletteKey: 'skintones' },
      { id: 'ebony', hex: '#4B2D20', label: 'Ebony', tier: 'premium', paletteKey: 'skintones' },
    ],
  },
  {
    key: 'pastel-member',
    colors: [
      { id: 'petal', hex: '#FBCFE8', label: 'Petal', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'powder', hex: '#BFDBFE', label: 'Powder', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'mist', hex: '#E5E7EB', label: 'Mist', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'butter', hex: '#FEF3C7', label: 'Butter', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'mintcream', hex: '#D1FAE5', label: 'Mint Cream', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'lilac', hex: '#DDD6FE', label: 'Lilac', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'pearl', hex: '#F5F3FF', label: 'Pearl', tier: 'premium', paletteKey: 'pasteluxe' },
      { id: 'softpeach', hex: '#FED7AA', label: 'Soft Peach', tier: 'premium', paletteKey: 'pasteluxe' },
    ],
  },
  {
    key: 'neon-premium',
    colors: [
      { id: 'electricpink', hex: '#FF4FD8', label: 'Electric Pink', tier: 'premium', paletteKey: 'neon' },
      { id: 'volt', hex: '#D9FF00', label: 'Volt', tier: 'premium', paletteKey: 'neon' },
      { id: 'laser', hex: '#00E5FF', label: 'Laser', tier: 'premium', paletteKey: 'neon' },
      { id: 'acid', hex: '#7CFF6B', label: 'Acid', tier: 'premium', paletteKey: 'neon' },
      { id: 'hotorange', hex: '#FF7A00', label: 'Hot Orange', tier: 'premium', paletteKey: 'neon' },
      { id: 'ultraviolet', hex: '#8B5CFF', label: 'Ultraviolet', tier: 'premium', paletteKey: 'neon' },
      { id: 'cyberred', hex: '#FF3B5C', label: 'Cyber Red', tier: 'premium', paletteKey: 'neon' },
      { id: 'toxicteal', hex: '#00FFC2', label: 'Toxic Teal', tier: 'premium', paletteKey: 'neon' },
    ],
  },
]];
const ALL_COLORS = COLOR_PAGES.flatMap((page) => page.flatMap((group) => group.colors));

const BottomColorPalette = ({ isOpen = true, zIndexClass = "z-[1060]" }) => {
  const { selectedColor, setSelectedColor } = useContext(ColorContext);
  const backendContext = useContext(BackendContext);
  const paletteScrollRef = useRef(null);
  const paletteDragStartXRef = useRef(0);
  const paletteScrollStartLeftRef = useRef(0);
  const isPaletteDraggingRef = useRef(false);
  const paletteAnimationTimeoutRef = useRef(null);

  const [palettePageIndex, setPalettePageIndex] = useState(0);
  const [paletteAnimating, setPaletteAnimating] = useState(false);
  const [palettePreviousIndex, setPalettePreviousIndex] = useState(null);
  const [lockedColorModalOpen, setLockedColorModalOpen] = useState(false);
  const [premiumColorModalOpen, setPremiumColorModalOpen] = useState(false);
  const [premiumColorPurchaseLoading, setPremiumColorPurchaseLoading] = useState(false);
  const [premiumColorPurchaseError, setPremiumColorPurchaseError] = useState('');
  const [pendingPremiumColor, setPendingPremiumColor] = useState(null);

  const isLoggedIn = () => !!backendContext?.userProfile?.sub;

  const getUnlockedPaletteKeys = useCallback(() => {
    const owned = backendContext?.userProfile?.ownedPalettes;
    if (Array.isArray(owned)) {
      return new Set(owned.map((entry) => String(entry)));
    }
    return new Set();
  }, [backendContext?.userProfile?.ownedPalettes]);

  const isColorUnlocked = useCallback((color) => {
    if (color.tier === 'guest') return true;
    if (color.tier === 'member') return isLoggedIn();
    const ownedPaletteKeys = getUnlockedPaletteKeys();
    return ownedPaletteKeys.has(color.paletteKey) || ownedPaletteKeys.has(color.id);
  }, [getUnlockedPaletteKeys, backendContext?.userProfile?.sub]);

  const getSelectedColorMeta = useCallback(() => {
    const normalizedSelectedColor = String(selectedColor || '').toLowerCase();
    const match = ALL_COLORS.find((color) => color.hex.toLowerCase() === normalizedSelectedColor);
    return match || ALL_COLORS[0];
  }, [selectedColor]);

  const getFirstUnlockedColor = useCallback(() => {
    const match = ALL_COLORS.find((color) => isColorUnlocked(color));
    return match || ALL_COLORS[0];
  }, [isColorUnlocked]);

  useEffect(() => {
    const selectedMeta = getSelectedColorMeta();
    if (!isColorUnlocked(selectedMeta) || String(selectedColor || '').toLowerCase() !== selectedMeta.hex.toLowerCase()) {
      const fallback = getFirstUnlockedColor();
      if (fallback && typeof setSelectedColor === 'function') {
        setSelectedColor(fallback.hex);
      }
    }
  }, [selectedColor, setSelectedColor, getFirstUnlockedColor, getSelectedColorMeta, isColorUnlocked]);

  useEffect(() => () => {
    clearTimeout(paletteAnimationTimeoutRef.current);
    window.removeEventListener('mousemove', onPaletteMouseMove);
    window.removeEventListener('mouseup', onPaletteMouseUp);
  }, []);

  const handlePaletteColorClick = (color) => {
    if (isColorUnlocked(color)) {
      setSelectedColor(color.hex);
      return;
    }

    if (color.tier === 'member' && !isLoggedIn()) {
      setLockedColorModalOpen(true);
      return;
    }

    if (color.tier === 'premium') {
      if (!isLoggedIn()) {
        setLockedColorModalOpen(true);
        return;
      }
      setPendingPremiumColor(color);
      setPremiumColorPurchaseError('');
      setPremiumColorModalOpen(true);
    }
  };

  const handlePremiumColorPurchase = async () => {
    if (!pendingPremiumColor) return;
    setPremiumColorPurchaseLoading(true);
    setPremiumColorPurchaseError('');
    try {
      const result = await backendContext.purchasePremiumColor(pendingPremiumColor.id);
      if (!result?.success) {
        setPremiumColorPurchaseError(result?.message || 'Unable to unlock this color right now.');
        return;
      }
      setPremiumColorModalOpen(false);
      setSelectedColor(pendingPremiumColor.hex);
      await backendContext.fetchUserProfile?.();
    } catch (error) {
      console.error(error);
      setPremiumColorPurchaseError('Unable to unlock this color right now.');
    } finally {
      setPremiumColorPurchaseLoading(false);
    }
  };

  const getPremiumColorCost = (color) => (
    PREMIUM_COLOR_COSTS[String(color?.id || '').toLowerCase()] ?? 5000
  );

  const renderPaletteSwatch = (color) => {
    const unlocked = isColorUnlocked(color);
    const isSelected = String(selectedColor || '').toLowerCase() === color.hex.toLowerCase();

    return (
      <button
        key={color.id}
        type="button"
        onClick={() => handlePaletteColorClick(color)}
        className={`relative h-8 w-8 shrink-0 rounded-lg border transition-all ${
          isSelected
            ? 'border-white ring-2 ring-white/70 ring-offset-1 ring-offset-slate-950'
            : 'border-slate-700 hover:border-slate-400'
        } ${!unlocked ? 'cursor-pointer' : ''}`}
        style={{ backgroundColor: color.hex }}
        aria-pressed={isSelected}
        title={unlocked ? color.label : color.tier === 'premium' ? `Unlock for ${getPremiumColorCost(color).toLocaleString()} Pixel Drops` : 'Log in to unlock'}
      >
        {!unlocked && (
          <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-slate-950/90 text-white shadow-sm">
            <Lock size={10} />
          </span>
        )}
      </button>
    );
  };

  const renderPalettePage = (pageIndex) => {
    const paletteGroups = COLOR_PAGES[pageIndex] ?? COLOR_PAGES[0];

    return (
      <div className="flex items-center gap-5 min-w-max rounded-2xl border border-slate-800/70 bg-slate-900/90 px-2.5 py-2 shadow-[0_10px_22px_rgba(2,6,23,0.24)]">
        {paletteGroups.map((group) => (
          <div key={group.key} className="flex items-center gap-2 min-w-max">
            {group.colors.map(renderPaletteSwatch)}
          </div>
        ))}
      </div>
    );
  };

  const getPaletteLayerClasses = (pageIndex) => {
    const isActive = pageIndex === palettePageIndex;
    const isPrevious = pageIndex === palettePreviousIndex;

    if (paletteAnimating) {
      if (isActive) return 'translate-y-0 scale-100 rotate-0 opacity-100 z-20 shadow-[0_14px_28px_rgba(2,6,23,0.32)]';
      if (isPrevious) return 'translate-y-4 scale-[0.975] -rotate-[2deg] opacity-52 z-10 shadow-[0_8px_18px_rgba(2,6,23,0.16)]';
    }

    return isActive
      ? 'translate-y-0 scale-100 rotate-0 opacity-100 z-20 shadow-[0_14px_28px_rgba(2,6,23,0.3)]'
      : 'translate-y-4 scale-[0.975] -rotate-[2deg] opacity-52 z-10 shadow-[0_8px_18px_rgba(2,6,23,0.16)]';
  };

  const renderPaletteDeck = () => (
    <div className="inline-grid min-w-max pr-2" style={{ perspective: '1200px', transformStyle: 'preserve-3d' }}>
      {COLOR_PAGES.map((_, pageIndex) => (
        <div
          key={`bottom-palette-page-${pageIndex}`}
          className={`col-start-1 row-start-1 origin-top-left transition-all duration-200 ease-out ${getPaletteLayerClasses(pageIndex)}`}
          style={{ transformStyle: 'preserve-3d' }}
        >
          {renderPalettePage(pageIndex)}
        </div>
      ))}
    </div>
  );

  const cyclePalettePage = () => {
    if (paletteAnimating) return;
    setPalettePreviousIndex(palettePageIndex);
    setPalettePageIndex((prev) => (prev + 1) % COLOR_PAGES.length);
    setPaletteAnimating(true);
    if (paletteScrollRef.current) {
      paletteScrollRef.current.scrollLeft = 0;
    }
    clearTimeout(paletteAnimationTimeoutRef.current);
    paletteAnimationTimeoutRef.current = setTimeout(() => {
      setPaletteAnimating(false);
      setPalettePreviousIndex(null);
    }, 200);
  };

  const onPaletteMouseDown = (e) => {
    const scroller = paletteScrollRef.current;
    if (!scroller) return;
    isPaletteDraggingRef.current = true;
    paletteDragStartXRef.current = e.clientX;
    paletteScrollStartLeftRef.current = scroller.scrollLeft;
    window.addEventListener('mousemove', onPaletteMouseMove);
    window.addEventListener('mouseup', onPaletteMouseUp);
  };

  const onPaletteMouseMove = (e) => {
    const scroller = paletteScrollRef.current;
    if (!isPaletteDraggingRef.current || !scroller) return;
    const deltaX = e.clientX - paletteDragStartXRef.current;
    scroller.scrollLeft = paletteScrollStartLeftRef.current - deltaX;
  };

  const onPaletteMouseUp = () => {
    isPaletteDraggingRef.current = false;
    window.removeEventListener('mousemove', onPaletteMouseMove);
    window.removeEventListener('mouseup', onPaletteMouseUp);
  };

  const selectedMeta = useMemo(() => getSelectedColorMeta(), [getSelectedColorMeta]);

  return (
    <>
      <div
        className={`fixed inset-x-0 bottom-3 pointer-events-none px-3 transition-all duration-300 ease-out ${zIndexClass} ${
          isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}
      >
        <div className="pointer-events-auto mx-auto w-[min(96vw,1180px)] rounded-2xl border border-slate-700 bg-slate-950/95 shadow-2xl backdrop-blur">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex items-center gap-2 shrink-0 rounded-xl border border-slate-800 bg-slate-900/80 px-2.5 py-1.5">
              <div
                className="h-6 w-6 rounded-md border border-white/15"
                style={{ backgroundColor: selectedMeta.hex }}
              />
              <div className="leading-tight">
                <div className="text-xs font-semibold text-white">{selectedMeta.label}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">
                  {selectedMeta.tier}
                </div>
              </div>
            </div>

            <div
              ref={paletteScrollRef}
              onMouseDown={onPaletteMouseDown}
              className={`min-w-0 flex-1 overflow-x-auto overflow-y-hidden select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${isPaletteDraggingRef.current ? 'cursor-grabbing' : 'cursor-grab'}`}
            >
              <div className="pb-3 pt-0.5">
                {renderPaletteDeck()}
              </div>
            </div>

            <button
              type="button"
              onClick={cyclePalettePage}
              disabled={paletteAnimating}
              className="h-8 w-8 shrink-0 rounded-full border border-slate-700 bg-slate-900/80 text-slate-300 transition hover:border-slate-500 hover:text-white disabled:cursor-default disabled:opacity-70"
              title={`Show color set ${((palettePageIndex + 1) % COLOR_PAGES.length) + 1}`}
              aria-label="Switch color set"
            >
              <RefreshCcw size={14} className={`mx-auto transition-transform duration-200 ${paletteAnimating ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <LoginRequiredModal
        isOpen={lockedColorModalOpen}
        onClose={() => setLockedColorModalOpen(false)}
      />

      <Modal isOpen={premiumColorModalOpen} onClose={() => setPremiumColorModalOpen(false)} backdrop="blur">
        <ModalContent className="bg-slate-950 text-white">
          <ModalHeader>Unlock Premium Color</ModalHeader>
          <ModalBody>
            <div className="flex items-center gap-3">
              <div
                className="h-10 w-10 rounded-lg border border-white/15"
                style={{ backgroundColor: pendingPremiumColor?.hex || '#111827' }}
              />
              <div>
                <div className="font-semibold">{pendingPremiumColor?.label || 'Premium Color'}</div>
                <div className="text-sm text-slate-300">
                  Unlock this color for {getPremiumColorCost(pendingPremiumColor).toLocaleString()} Pixel Drops.
                </div>
              </div>
            </div>
            {premiumColorPurchaseError ? (
              <div className="rounded-xl border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-200">
                {premiumColorPurchaseError}
              </div>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={() => setPremiumColorModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handlePremiumColorPurchase}
              isLoading={premiumColorPurchaseLoading}
            >
              Unlock
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BottomColorPalette;
